import { useState } from 'react';
import { Check, Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import Modal from './Modal.jsx';
import FormField from './FormField.jsx';
import {
  useEntityIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
} from '../hooks/useEntityIntegrations.js';
import { PROVIDER_LABELS } from '../lib/providers.js';

// ── Per-kind field schemas ────────────────────────────────────────────────────

const KIND_SCHEMAS = {
  qonto: [
    { key: 'orgSlug', label: 'Org Slug', required: true, type: 'text', placeholder: 'legendary-parts-xxxx' },
    { key: 'apiSecret', label: 'API Secret', required: true, type: 'password', placeholder: '••••••••' },
    { key: 'baseUrl', label: 'Base URL (optionnel)', required: false, type: 'text', placeholder: 'https://thirdparty.qonto.com/v2' },
  ],
  wise: [
    { key: 'apiToken', label: 'API Token', required: true, type: 'password', placeholder: '••••••••' },
    { key: 'profileId', label: 'Profile ID (optionnel)', required: false, type: 'text', placeholder: '12345678' },
    { key: 'baseUrl', label: 'Base URL (optionnel)', required: false, type: 'text', placeholder: 'https://api.wise.com' },
    { key: 'privateKeyPath', label: 'Chemin clé privée (optionnel)', required: false, type: 'text', placeholder: '/etc/wise/private.pem' },
  ],
  shopify: [
    { key: 'domain', label: 'Domaine Shopify', required: true, type: 'text', placeholder: 'legendary-parts.myshopify.com' },
    { key: 'clientId', label: 'Client ID', required: true, type: 'text', placeholder: 'shp_...' },
    { key: 'clientSecret', label: 'Client Secret', required: true, type: 'password', placeholder: '••••••••' },
    { key: 'version', label: 'Version API (optionnel)', required: false, type: 'text', placeholder: '2024-04' },
  ],
  revolut: [
    { key: 'clientId', label: 'Client ID', required: true, type: 'text' },
    { key: 'privateKeyPem', label: 'Clé privée (PEM)', required: true, type: 'textarea' },
    { key: 'issuer', label: 'Issuer (domaine JWT)', required: true, type: 'text' },
    { key: 'refreshToken', label: 'Refresh token', required: true, type: 'password' },
    { key: 'baseUrl', label: 'Base URL (optionnel)', required: false, type: 'text', placeholder: 'https://b2b.revolut.com/api/1.0' },
  ],
};

const KIND_LABELS = { ...PROVIDER_LABELS, shopify: 'Shopify' };
const KINDS = ['qonto', 'wise', 'shopify', 'revolut'];

// ── PasswordInput helper ──────────────────────────────────────────────────────

function PasswordInput({ id, value, onChange, placeholder, className }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="new-password"
        className={`${className} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        aria-label={visible ? 'Masquer' : 'Afficher'}
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

// ── InlineCredentialsForm ─────────────────────────────────────────────────────

function InlineCredentialsForm({ kind, onSave, onCancel, isPending }) {
  const fields = KIND_SCHEMAS[kind];
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.key, '']))
  );
  const [errors, setErrors] = useState({});

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500';

  function handleSubmit(e) {
    e.preventDefault();
    const newErrors = {};
    fields.forEach((f) => {
      if (f.required && !values[f.key]?.trim()) {
        newErrors[f.key] = 'Champ requis';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Only pass non-empty values
    const credentials = Object.fromEntries(
      fields
        .filter((f) => values[f.key]?.trim())
        .map((f) => [f.key, f.key === 'profileId' ? Number(values[f.key]) : values[f.key]])
    );
    onSave(credentials);
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {fields.map((f) => (
        <FormField key={f.key} label={f.label} htmlFor={`cred-${kind}-${f.key}`} required={f.required} error={errors[f.key]}>
          {f.type === 'password' ? (
            <PasswordInput
              id={`cred-${kind}-${f.key}`}
              value={values[f.key]}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.key]: e.target.value }));
                setErrors((err) => ({ ...err, [f.key]: undefined }));
              }}
              placeholder={f.placeholder}
              className={inputClass}
            />
          ) : f.type === 'textarea' ? (
            <textarea
              id={`cred-${kind}-${f.key}`}
              rows={4}
              value={values[f.key]}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.key]: e.target.value }));
                setErrors((err) => ({ ...err, [f.key]: undefined }));
              }}
              placeholder={f.placeholder}
              className={`${inputClass} font-mono text-xs resize-y`}
            />
          ) : (
            <input
              id={`cred-${kind}-${f.key}`}
              type="text"
              value={values[f.key]}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.key]: e.target.value }));
                setErrors((err) => ({ ...err, [f.key]: undefined }));
              }}
              placeholder={f.placeholder}
              className={inputClass}
            />
          )}
        </FormField>
      ))}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {isPending ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}

// ── KindSection ───────────────────────────────────────────────────────────────

function KindSection({ kind, integration, entityId }) {
  const [mode, setMode] = useState(null); // null | 'add' | 'edit'
  const createIntegration = useCreateIntegration(entityId);
  const updateIntegration = useUpdateIntegration(entityId);
  const deleteIntegration = useDeleteIntegration(entityId);

  const configured = integration?.configured ?? false;
  const status = integration?.status;

  async function handleSave(credentials) {
    if (configured) {
      await updateIntegration.mutateAsync({ kind, credentials });
    } else {
      await createIntegration.mutateAsync({ kind, credentials });
    }
    setMode(null);
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer l'intégration ${KIND_LABELS[kind]} ?`)) return;
    await deleteIntegration.mutateAsync(kind);
  }

  const isPending =
    createIntegration.isPending || updateIntegration.isPending || deleteIntegration.isPending;

  const statusBadge = {
    active: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    inactive: 'bg-slate-100 text-slate-500',
  }[status] ?? 'bg-slate-100 text-slate-500';

  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{KIND_LABELS[kind]}</span>
          {configured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              <Check size={11} />
              Configuré
            </span>
          )}
          {configured && status && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge}`}>
              {status}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {configured ? (
            <>
              <button
                type="button"
                onClick={() => setMode(mode === 'edit' ? null : 'edit')}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                aria-label={`Modifier credentials ${KIND_LABELS[kind]}`}
              >
                <Pencil size={12} />
                Modifier
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                aria-label={`Supprimer intégration ${KIND_LABELS[kind]}`}
              >
                <Trash2 size={12} />
                Supprimer
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setMode(mode === 'add' ? null : 'add')}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-700"
              aria-label={`Ajouter intégration ${KIND_LABELS[kind]}`}
            >
              <Plus size={12} />
              Ajouter
            </button>
          )}
        </div>
      </div>

      {mode && (
        <InlineCredentialsForm
          kind={kind}
          onSave={handleSave}
          onCancel={() => setMode(null)}
          isPending={createIntegration.isPending || updateIntegration.isPending}
        />
      )}

      {(createIntegration.error || updateIntegration.error || deleteIntegration.error) && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {(createIntegration.error ?? updateIntegration.error ?? deleteIntegration.error)?.message}
        </p>
      )}
    </div>
  );
}

// ── IntegrationsModal (public) ────────────────────────────────────────────────

export default function IntegrationsModal({ open, onClose, entity }) {
  const { data: integrations, isLoading, error } = useEntityIntegrations(
    open ? entity?.id : null
  );

  const integrationByKind = (kind) =>
    (integrations ?? []).find((i) => i.kind === kind) ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={entity ? `Intégrations — ${entity.name}` : 'Intégrations'}
      size="md"
    >
      {isLoading && (
        <p className="py-6 text-center text-sm text-slate-500">Chargement...</p>
      )}
      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error.message}
        </p>
      )}
      {!isLoading && !error && entity && (
        <div className="space-y-3">
          {KINDS.map((kind) => (
            <KindSection
              key={kind}
              kind={kind}
              integration={integrationByKind(kind)}
              entityId={entity.id}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
